/**
 * Battle panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import {
	PS, PSRoom, type RoomOptions, type RoomID, Config, type BattlePanelLayout, consoleModeOn,
} from "./client-main";
import { PSIcon, PSPanelWrapper, PSRoomPanel } from "./panels";
import { ChatLog, ChatRoom, ChatTextEntry, ChatUserList } from "./panel-chat";
import { FormatDropdown } from "./panel-mainmenu";
import { Battle, type Pokemon, type ServerPokemon } from "./battle";
import { BattleScene } from "./battle-animations";
import { Dex, toID, type ID } from "./battle-dex";
import {
	BattleChoiceBuilder, type BattleRequestActivePokemon, type BattleRequestSideInfo,
	type BattleRequest, type BattleMoveRequest, type BattleSwitchRequest, type BattleTeamRequest,
} from "./battle-choices";
import type { Args } from "./battle-text-parser";
import { ModifiableValue } from "./battle-tooltips";
import { Net } from "./client-connection";
import { BattleLog } from "./battle-log";
import { interfaceTheme } from "./signal-palettes";
import { PSTabletop } from "./tabletop-link";
import { PSConsole } from "./console-nav";
import { ConsoleCloseButton, ConsoleScreenHead, menuScreensOn } from "./console-screens";

/** Galar's move cards: the move in words, and its effectiveness badge. */
interface GalarMoveGuide {
	meta: string;
	eff?: { key: 'high' | 'ok' | 'low' | 'none', icon: string, label: string };
}

type BattleDesc = {
	id: RoomID,
	minElo?: number | string,
	p1?: string,
	p2?: string,
	p3?: string,
	p4?: string,
};
export class BattlesRoom extends PSRoom {
	override readonly classType = 'battles';
	/** null means still loading */
	format = '';
	filters = '';
	battles: BattleDesc[] | null = null;
	constructor(options: RoomOptions) {
		super(options);
		this.refresh();
		// If graphics preference is set to use BW sprites
		if (PS.prefs.bwgfx) {
			Dex.loadSpriteData('bw');
		}
	}
	setFormat(format: string) {
		if (format === this.format) return this.refresh();
		this.battles = null;
		this.format = format;
		this.update(null);
		this.refresh();
	}
	refresh() {
		PS.send(`/cmd roomlist ${toID(this.format)}, ${this.filters}`);
	}
}

class BattlesPanel extends PSRoomPanel<BattlesRoom> {
	static readonly id = 'battles';
	static readonly routes = ['battles'];
	static readonly Model = BattlesRoom;
	static readonly location = 'right';
	static readonly icon = <i class="fa fa-caret-square-o-right" aria-hidden></i>;
	static readonly title = 'Battles';
	refresh = () => {
		this.props.room.refresh();
	};
	/** Console mode keeps the list fresh while it's open. */
	consoleTimer: ReturnType<typeof setInterval> | null = null;
	override componentDidMount() {
		super.componentDidMount();
		if (!menuScreensOn()) return;
		PS.mainmenu.requestTrainers();
		this.consoleTimer = setInterval(() => {
			if (!document.hidden && menuScreensOn()) {
				this.props.room.refresh();
				PS.mainmenu.requestTrainers();
			}
		}, 10000);
	}
	override componentWillUnmount() {
		if (this.consoleTimer) clearInterval(this.consoleTimer);
		super.componentWillUnmount();
	}
	/**
	 * Console mode's Watch: every battle on, as big cards (the room list,
	 * plus the trainers' own battles, which include private ones they can see).
	 */
	renderConsole() {
		const room = this.props.room;
		const seen = new Set<string>();
		const battles: { id: string, p1: string, p2: string }[] = [];
		for (const t of PS.mainmenu.trainersCache || []) {
			for (const b of t.battles) {
				if (seen.has(b.id)) continue;
				seen.add(b.id);
				battles.push({ id: b.id, p1: b.p1, p2: b.p2 });
			}
		}
		for (const b of room.battles || []) {
			if (seen.has(b.id)) continue;
			seen.add(b.id);
			battles.push({ id: b.id, p1: b.p1 || '', p2: b.p2 || '' });
		}
		const loading = !room.battles && !PS.mainmenu.trainersCache;
		return <PSPanelWrapper room={room}>
			<div class="kc-screen" data-kc-screen="watch">
				<ConsoleScreenHead
					icon="eye" title="Watch"
					sub={loading ? 'Looking for battles...' : !battles.length ? 'No battles are on right now.' :
						battles.length === 1 ? '1 battle is on' : `${battles.length} battles are on`}
				>
					<button class="button kc-back" name="refresh" onClick={this.refresh} aria-label="Refresh">
						<i class="fa fa-refresh" aria-hidden></i>
					</button>
					<ConsoleCloseButton />
				</ConsoleScreenHead>
				{battles.length > 0 && <div class="kc-cards">{battles.map((b, i) => {
					const format = b.id.split('-')[1] || '';
					return <a
						class="kc-card kc-watch" href={`/${b.id}`} style={`--kc-i:${i}`} data-console-default={i === 0 || undefined}
					>
						<span class="kc-live" aria-hidden><i></i> Live</span>
						<span class="kc-watch-names">
							<strong>{b.p1 || '?'}</strong>
							<span class="kc-vs" aria-hidden>VS</span>
							<strong>{b.p2 || '?'}</strong>
						</span>
						<small class="kc-message">{window.BattleLog ? BattleLog.formatName(format) : format}</small>
					</a>;
				})}</div>}
				{!loading && !battles.length && <p class="kc-empty">When trainers battle, their battles show up here to watch.</p>}
			</div>
		</PSPanelWrapper>;
	}
	changeFormat = (e: Event) => {
		const value = (e.target as HTMLButtonElement).value;
		this.props.room.setFormat(value);
	};
	applyFilters = (e: Event) => {
		e.preventDefault();
		const minElo = this.base?.querySelector<HTMLInputElement>(`select[name=elofilter]`)?.value;
		const searchPrefix = this.base?.querySelector<HTMLInputElement>(`input[name=prefixsearch]`)?.value;
		this.props.room.filters = `${minElo || ''},${searchPrefix || ''}`;
		this.refresh();
	};
	renderBattleLink(battle: BattleDesc) {
		const format = battle.id.split('-')[1];
		const minEloMessage = typeof battle.minElo === 'number' ? `rated ${battle.minElo}` : battle.minElo;
		return <div key={battle.id}><a href={`/${battle.id}`} class="blocklink">
			{minEloMessage && <small style="float:right">({minEloMessage})</small>}
			<small>[{format}]</small><br />
			<em class="p1">{battle.p1}</em> <small class="vs">vs.</small> <em class="p2">{battle.p2}</em>
		</a></div>;
	}
	override render() {
		if (menuScreensOn()) return this.renderConsole();
		const room = this.props.room;
		return <PSPanelWrapper room={room}><div class="pad">
			<button class="button" style="float:right;font-size:10pt;margin-top:3px" name="closeRoom">
				<i class="fa fa-times" aria-hidden></i> Close
			</button>
			<div class="roomlist">
				<p>
					<button class="button" name="refresh" onClick={this.refresh}>
						<i class="fa fa-refresh" aria-hidden></i> Refresh
					</button> {}
					<span
						style={Dex.getPokemonIcon('meloetta-pirouette') + ';display:inline-block;vertical-align:middle'} class="picon"
						title="Meloetta is PS's mascot! The Pirouette forme is Fighting-type, and represents our battles."
					></span>
				</p>

				<p>
					<label class="label">Format:</label><FormatDropdown onChange={this.changeFormat} placeholder="(All formats)" />
				</p>
				<label class="label">
					Minimum Elo: <select name="elofilter" class="select" onChange={this.applyFilters}>
						<option value="none">None</option><option value="1100">1100</option><option value="1300">1300</option>
						<option value="1500">1500</option><option value="1700">1700</option><option value="1900">1900</option>
					</select>
				</label>

				<form class="search" onSubmit={this.applyFilters}>
					<p>
						<input type="text" name="prefixsearch" class="textbox" placeholder="Username prefix" autocomplete="off" />
						<button type="submit" class="button">Search</button>
					</p>
				</form>
				<div class="list">{!room.battles ? (
					<p>Loading...</p>
				) : !room.battles.length ? (
					<p>No battles are going on</p>
				) : (<>
					<p>{room.battles.length === 100 ?
						`100+` : room.battles.length} {room.battles.length > 1 ? `battles` : `battle`}</p>
					{room.battles.map(battle => this.renderBattleLink(battle))}
				</>
				)}</div>
			</div>
		</div></PSPanelWrapper>;
	}
}

export class BattleRoom extends ChatRoom {
	override readonly classType = 'battle';
	declare pmTarget: null;
	declare challengeMenuOpen: false;
	declare challengingFormat: null;
	declare challengedFormat: null;

	override battle: Battle = null!;
	/** null if spectator, otherwise current player's info */
	side: BattleRequestSideInfo | null = null;
	request: BattleRequest | null = null;
	choices: BattleChoiceBuilder | null = null;
	requests: Partial<Record<BattleRequestSideInfo['id'], BattleRequest>> = {};
	choicesBySide: Partial<Record<BattleRequestSideInfo['id'], BattleChoiceBuilder>> = {};
	autoTimerActivated: boolean | null = null;
	requireForfeit = false;
	/** should be false if we joined right after accepting or challenging a battle,
	  * and true if we refreshed and rejoined a battle.
		* null = initializing, we don't know yet */
	rejoining: boolean | null = null;
	overlayActive: 'move' | 'switch' | null = null;

	override interruptClose(explicit?: boolean, elem?: HTMLElement | null) {
		if (this.isPlaying() || this.requireForfeit) {
			PS.join('forfeitbattle' as RoomID, { parentElem: elem, parentRoomid: this.id });
			return `You are still in ${this.title}`;
		}
		return super.interruptClose(explicit, elem);
	}
	isPlaying() {
		return this.battle && !this.battle.ended && this.request && this.connectMode !== 'deleted';
	}
	updateChoiceNotification() {
		let oName = this.battle?.farSide.name;
		if (oName) oName = " against " + oName;
		let title = '';
		let body = '';
		switch (this.request?.requestType) {
		case 'move':
			title = "Your move!";
			body = "Move in your battle" + oName;
			break;
		case 'switch':
			title = "Your switch!";
			body = "Switch in your battle" + oName;
			break;
		case 'team':
			title = "Team preview!";
			body = "Choose your team order in your battle" + oName;
			break;
		}

		if (!this.choices || this.choices.isDone()) body = '';

		const current = this.notifications.find(notification => notification.id === 'choice');
		if ((current?.body || '') === body) return;

		if (!body) {
			this.dismissNotification('choice');
		} else {
			this.notify({ title, body, id: 'choice', noAutoDismiss: true });
		}
	}

	override handleReconnect(): boolean | void {
		if (this.battle) {
			this.battle.stepQueue = [];
			this.battle.preemptStepQueue = [];
			this.battle.resetStep();
		}
		this.side = null;
		this.request = null;
		this.choices = null;
		this.requests = {};
		this.choicesBySide = {};
		this.updateChoiceNotification();
		return false;
	}

	override destroy() {
		this.request = null;
		this.choices = null;
		this.requests = {};
		this.choicesBySide = {};
		super.destroy();
	}
	setActiveRequest(request: BattleRequest, choices?: BattleChoiceBuilder) {
		if (request.side) {
			this.battle.myPokemon = request.side.pokemon;
			this.battle.setViewpoint(request.side.id);
			this.side = request.side;
		}
		if (request.ally) this.battle.myAllyPokemon = request.ally.pokemon;
		this.request = request;
		this.choices = choices || (request.side && this.choicesBySide[request.side.id]) || new BattleChoiceBuilder(request);
		this.overlayActive = null;
	}
	switchControlledSide(sideid: BattleRequestSideInfo['id']) {
		const request = this.requests[sideid];
		if (!request) return;
		this.setActiveRequest(request);
		this.updateChoiceNotification();
		this.update(null);
	}
	selectNextRequest() {
		const next = Object.values(this.requests).find(request => {
			if (!request?.side || request === this.request) return false;
			return !this.choicesBySide[request.side.id]?.isDone();
		});
		if (next) this.setActiveRequest(next);
	}

	loadReplay() {
		const replayid = this.id.slice(7);
		Net(`https://replay.pokemonshowdown.com/${replayid}.json`).get().catch(() => '').then(data => {
			try {
				const replay = JSON.parse(data);
				this.title = `[${replay.format}] ${replay.players.join(' vs. ')}`;
				this.battle.stepQueue = replay.log.split('\n');
				this.battle.atQueueEnd = false;
				this.battle.pause();
				this.battle.seekTurn(0);
				this.connectMode = null;
				this.connectError = null;
				this.update(null);
			} catch {
				this.connectError = `Battle "${replayid}" not found`;
				if (!this.battle.stepQueue.length) {
					this.battle.scene.message(
						`<div class="broadcast-red pad"><strong>${BattleLog.escapeHTML(this.connectError)}</strong></div><br />` +
						`The battle you're looking for has expired. Battles expire after 15 minutes of inactivity unless they're saved.<br /><br />` +
						`In the future, remember to click "Save replay" to save a replay permanently.`
					);
				}
				this.update(null);
			}
		});
	}
}

class BattleDiv extends preact.Component<{ room: BattleRoom }> {
	override shouldComponentUpdate() {
		return false;
	}
	override componentDidMount() {
		const room = this.props.room;
		if (room.battle) {
			this.base!.replaceChild(room.battle.scene.$frame![0], this.base!.firstChild!);
		}
	}
	override render() {
		return <div><div class="battle"></div></div>;
	}
}

class TimerButton extends preact.Component<{ room: BattleRoom, top: number }> {
	timerInterval: number | null = null;
	override componentWillUnmount() {
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}
	secondsToTime(seconds: number | true) {
		if (seconds === true) return '-:--';
		const minutes = Math.floor(seconds / 60);
		seconds -= minutes * 60;
		return `${minutes}:${(seconds < 10 ? '0' : '')}${seconds}`;
	}
	render() {
		let time = 'Timer';
		const room = this.props.room;
		if (!this.timerInterval && room.battle.kickingInactive) {
			this.timerInterval = setInterval(() => {
				if (room.choices?.isDone()) return;
				if (typeof room.battle.kickingInactive === 'number' && room.battle.kickingInactive > 1) {
					room.battle.kickingInactive--;
					if (room.battle.graceTimeLeft) room.battle.graceTimeLeft--;
					else if (room.battle.totalTimeLeft) room.battle.totalTimeLeft--;
				}
				this.forceUpdate();
			}, 1000);
		} else if (this.timerInterval && !room.battle.kickingInactive) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}

		let timerTicking = (room.battle.kickingInactive &&
			room.request && room.request.requestType !== "wait" && (room.choices && !room.choices.isDone())) ?
			' timerbutton-on' : '';

		if (room.battle.kickingInactive) {
			const secondsLeft = room.battle.kickingInactive;
			time = this.secondsToTime(secondsLeft);
			if (secondsLeft !== true) {
				if (secondsLeft <= 10 && timerTicking) {
					timerTicking = ' timerbutton-critical';
				}

				if (room.battle.totalTimeLeft) {
					const totalTime = this.secondsToTime(room.battle.totalTimeLeft);
					time += ` |  ${totalTime} total`;
				}
			}
		}

		return <button
			style={{ position: "absolute", right: '10px', top: `${this.props.top}px` }}
			data-href="battletimer" class={`button${timerTicking}`} role="timer"
		>
			<i class="fa fa-hourglass-start" aria-hidden></i> {time}
		</button>;
	}
};

class BattlePanel extends PSRoomPanel<BattleRoom> {
	static readonly id = 'battle';
	static readonly routes = ['battle-*', 'game-*'];
	static readonly Model = BattleRoom;
	static handleDrop(ev: DragEvent) {
		const file = ev.dataTransfer?.files?.[0];
		if (file?.type === 'text/html') {
			let roomNum = 1;
			for (; roomNum < 100; roomNum++) {
				if (!PS.rooms[`battle-uploaded-${roomNum}`]) break;
			}
			file.text().then(html => {
				const titleStart = html.indexOf('<title>');
				const titleEnd = html.indexOf('</title>');
				let title = 'Uploaded Replay';
				if (titleStart >= 0 && titleEnd > titleStart) {
					title = html.slice(titleStart + 7, titleEnd - 1);
					const colonIndex = title.indexOf(':');
					const hyphenIndex = title.lastIndexOf('-');
					if (hyphenIndex > colonIndex + 2) {
						title = title.substring(colonIndex + 2, hyphenIndex - 1);
					} else {
						title = title.substring(colonIndex + 2);
					}
				}
				const index1 = html.indexOf('<script type="text/plain" class="battle-log-data">');
				const index2 = html.indexOf('<script type="text/plain" class="log">');
				if (index1 < 0 && index2 < 0) {
					PS.alert("Unrecognized HTML file: Only replay files are supported.");
					return;
				}
				if (index1 >= 0) {
					html = html.slice(index1 + 50);
				} else if (index2 >= 0) {
					html = html.slice(index2 + 38);
				}
				const index3 = html.indexOf('</script>');
				html = html.slice(0, index3);
				html = html.replace(/\\\//g, '/');

				PS.join(`battle-uploaded-${roomNum}` as RoomID);
				const room = PS.rooms[`battle-uploaded-${roomNum}`] as BattleRoom;
				if (!room) return;

				room.title = title;
				room.connectMode = null;
				PS.receive(`>battle-uploaded-${roomNum}\n${html}`);
			});
			return true;
		}
	}
	/** last displayed team. will not show the most recent request until the last one is gone. */
	team: ServerPokemon[] | null = null;
	mobileChatShown = false;
	showMobileChat = () => {
		this.mobileChatShown = true;
		this.forceUpdate();
	};
	showMobileBattle = () => {
		this.mobileChatShown = false;
		this.forceUpdate();
	};
	send = (text: string, elem?: HTMLElement) => {
		this.props.room.send(text, elem);
	};
	focusIfNoSelection = () => {
		if (window.getSelection?.()?.type === 'Range') return;
		this.focus();
	};
	onKey = (e: KeyboardEvent) => {
		if (e.keyCode === 33) { // Pg Up key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop - chatLog.offsetHeight + 60;
			return true;
		} else if (e.keyCode === 34) { // Pg Dn key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop + chatLog.offsetHeight - 60;
			return true;
		}
		return false;
	};
	toggleBoostedMove = (e: Event) => {
		const checkbox = e.currentTarget as HTMLInputElement;
		const choices = this.props.room.choices;
		if (!choices) return; // shouldn't happen
		switch (checkbox.name) {
		case 'mega':
			choices.current.mega = checkbox.checked;
			break;
		case 'megax':
			choices.current.megax = checkbox.checked;
			choices.current.megay = false;
			break;
		case 'megay':
			choices.current.megay = checkbox.checked;
			choices.current.megax = false;
			break;
		case 'ultra':
			choices.current.ultra = checkbox.checked;
			break;
		case 'z':
			choices.current.z = checkbox.checked;
			break;
		case 'max':
			choices.current.max = checkbox.checked;
			break;
		case 'tera':
			choices.current.tera = checkbox.checked;
			break;
		}
		this.props.room.update(null);
	};
	override componentDidMount() {
		const room = this.props.room;
		const $elem = $(this.base!);
		const battle = (room.battle ||= new Battle({
			id: room.id as any,
			$frame: $elem.find('.battle'),
			$logFrame: $elem.find('.battle-log'),
			log: room.backlog?.map(args => '|' + args.join('|')),
		}));
		const scene = battle.scene as BattleScene;
		room.backlog = null;
		room.log ||= scene.log;
		room.log.getHighlight = room.handleHighlight;
		scene.tooltips.unlisten(scene.$frame);
		scene.tooltips.listen(this.base!);
		super.componentDidMount();
		if (!PS.prefs.spectatefromstart) battle.seekTurn(Infinity);
		if (PS.prefs.autohardcore) {
			battle.setHardcoreMode(true);
		}
		battle.subscribe(() => this.forceUpdate());
	}
	override componentWillUnmount() {
		const scene = this.props.room.battle?.scene as BattleScene | undefined;
		if (this.base) scene?.tooltips.unlisten(this.base);
		super.componentWillUnmount();
	}
	battleHeight = 360;
	updateLayout() {
		if (!this.base) return;
		const room = this.props.room;
		if (!room.width) return;
		const { battleHeight } = this.chooseLayout();
		this.battleHeight = battleHeight;
		if (battleHeight !== 360) {
			room.battle?.scene.$frame!.css('transform', `scale(${battleHeight / 360})`);
		} else {
			room.battle?.scene.$frame!.css('transform', 'none');
		}
		// Drawn bigger than 640x360: 2D sprites scale by nearest neighbor (battle.css).
		room.battle?.scene.$frame!.attr('data-upscaled', battleHeight > 360 ? '' : null);
	}
	chooseLayout(): {
		layout: BattlePanelLayout,
		battleHeight: number,
		battleWidth: number,
		overlayControls: boolean,
	} {
		const room = this.props.room;
		return PS.chooseBattleLayout(room.width, room.height, PS.prefs.battlelayout);
	}
	fastForwardIfRejoining() {
		const room = this.props.room;
		if (!room.rejoining || !room.side) return;
		room.rejoining = false;
		room.battle.seekTurn(Infinity);
	}
	override receiveLine(args: Args) {
		const room = this.props.room;
		switch (args[0]) {
		case 'cantleave':
			room.requireForfeit = true;
			return;
		case 'allowleave':
			room.requireForfeit = false;
			return;
		case 'initdone':
			if (!PS.prefs.spectatefromstart) room.battle.seekTurn(Infinity);
			return;
		case 'request':
			this.receiveRequest(args[1] ? JSON.parse(args[1]) : null);
			return;
		case 'win': case 'tie':
			this.receiveRequest(null);
			// Once the line is in the battle: a tournament match reports its result.
			setTimeout(() => PSTabletop.battleEnded(room.id, room.battle, !!room.side));
			break;
		case 'c': case 'c:': case 'chat': case 'chatmsg': case 'inactive':
			room.battle.instantAdd('|' + args.join('|'));
			return;
		case 'error':
			if (args[1].startsWith('[Invalid choice]') && room.request) {
				room.choices = new BattleChoiceBuilder(room.request);
				if (room.request.side) room.choicesBySide[room.request.side.id] = room.choices;
				room.updateChoiceNotification();
				room.update(null);
			}
			break;
		case 'sentchoice':
			{
				const sentRequest = args[3] ? room.requests[args[3] as BattleRequestSideInfo['id']] : (
					args[2] ? Object.values(room.requests).find(request => `${request?.rqid}` === args[2]) : room.request
				);
				if (!sentRequest) return;
				let choices = new BattleChoiceBuilder(sentRequest);
				const possibleError = choices.addChoices(args[1]);
				if (possibleError || !choices.isDone()) {
					choices = new BattleChoiceBuilder(sentRequest);
					choices.serializedChoice = args[1];
				}
				if (sentRequest.side) room.choicesBySide[sentRequest.side.id] = choices;
				if (sentRequest === room.request) room.choices = choices;
			}
			room.updateChoiceNotification();
			room.update(null);
			return;
		}
		room.battle.add('|' + args.join('|'));
		if (PS.prefs.noanim) this.props.room.battle.seekTurn(Infinity);
	}
	receiveRequest(request: BattleRequest | null) {
		const room = this.props.room;
		if (!request) {
			room.request = null;
			room.choices = null;
			room.requests = {};
			room.choicesBySide = {};
			room.updateChoiceNotification();
			return;
		}

		if (PS.prefs.autotimer && !room.battle.kickingInactive && !room.autoTimerActivated) {
			this.send('/timer on');
			room.autoTimerActivated = true;
		}

		BattleChoiceBuilder.fixRequest(request, room.battle);
		if (request.side) {
			const wasPlayer = !!room.side;
			room.requests[request.side.id] = request;
			const choices = new BattleChoiceBuilder(request);
			room.choicesBySide[request.side.id] = choices;
			if (!room.request || room.request.side?.id === request.side.id ||
				room.choices?.isDone() || room.request.requestType === 'wait') {
				room.setActiveRequest(request, choices);
			}
			if (!wasPlayer) this.fastForwardIfRejoining();
		}
		// A reconnect can send `|sentchoice|` immediately after `|request|`.
		// Wait until the entire protocol message has been processed before notifying.
		Promise.resolve().then(() => room.updateChoiceNotification());
		room.update(null);
	}
	renderConnectError() {
		const room = this.props.room;
		if (room.connectMode !== 'deleted' && room.connectMode !== 'not-found') {
			return null;
		}
		return <div class="pad"><div class="broadcast-red pad">
			<h3>{room.connectError || "Error"}</h3>
			<p class="buttonbar"><button class="button" data-cmd="/close"><strong>Close</strong></button></p>
		</div></div>;
	}
	switchControlledSide = (ev: Event) => {
		const sideid = (ev.currentTarget as HTMLButtonElement).value as BattleRequestSideInfo['id'];
		this.props.room.switchControlledSide(sideid);
	};
	renderControllerTabs(overlayVersion = false) {
		const room = this.props.room;
		const requests = Object.values(room.requests).filter(request => request?.side);
		if (requests.length < 2) return null;
		return <div class={`battle-controller-tabs${overlayVersion ? ' battle-controller-tabs-overlay' : ''}`}>
			{requests.map(request => <button
				class={`button${request === room.request ? ' cur' : ''}`}
				value={request.side!.id} onClick={this.switchControlledSide}
				aria-pressed={request === room.request}
			>
				<i class="fa fa-user" aria-hidden></i> {request.side!.name}
				{room.choicesBySide[request.side!.id]?.isDone() && <i class="fa fa-check" aria-hidden></i>}
			</button>)}
		</div>;
	}
	renderControls(overlayVersion = false, hidePlayerControls = false) {
		const room = this.props.room;
		if (!room.battle) return null;
		if (overlayVersion) {
			if (!room.side || !room.request || room.battle.ended) return null;
			return <>{this.renderControllerTabs(true)}{this.renderPlayerControls(room.request, true)}</>;
		}
		if (room.battle.ended) return this.renderAfterBattleControls();
		if (room.side && room.request) {
			if (hidePlayerControls) return null;
			return <>{this.renderControllerTabs()}{this.renderPlayerControls(room.request)}</>;
		}
		if (room.battle.stepQueue.length === 0) return null;

		const atStart = !room.battle.started;
		const atEnd = room.battle.atQueueEnd;
		return <div class="inline-controls">
			<p>
				{atEnd ? (
					<button class="button disabled" aria-disabled data-cmd="/play" style="min-width:4.5em">
						<i class="fa fa-play" aria-hidden></i><br />Play
					</button>
				) : room.battle.paused ? (
					<button class="button" data-cmd="/play" style="min-width:4.5em">
						<i class="fa fa-play" aria-hidden></i><br />Play
					</button>
				) : (
					<button class="button" data-cmd="/pause" style="min-width:4.5em">
						<i class="fa fa-pause" aria-hidden></i><br />Pause
					</button>
				)} {}
				{!room.battle.hardcoreMode && <>
					<button class={"button button-first" + (atStart ? " disabled" : "")} data-cmd="/ffto 0" style="margin-right:2px">
						<i class="fa fa-undo" aria-hidden></i><br />First turn
					</button>
					<button class={"button button-first" + (atStart ? " disabled" : "")} data-cmd="/ffto -1">
						<i class="fa fa-step-backward" aria-hidden></i><br />Prev turn
					</button>
					<button class={"button button-last" + (atEnd ? " disabled" : "")} data-cmd="/ffto +1" style="margin-right:2px">
						<i class="fa fa-step-forward" aria-hidden></i><br />Skip turn
					</button>
					<button class={"button button-last" + (atEnd ? " disabled" : "")} data-cmd="/ffto end">
						<i class="fa fa-fast-forward" aria-hidden></i><br />Skip to end
					</button>
				</>}
			</p>
			<p>
				<button class="button" data-cmd="/switchsides">
					<i class="fa fa-random" aria-hidden></i> Switch viewpoint
				</button> {}
				{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">
					<i class="fa fa-random" aria-hidden></i> Go to turn
				</button>}
			</p>
		</div>;
	}
	renderMoveButton(props: {
		name: string, cmd: string, type: Dex.TypeName, tags: string, tooltip: string,
		moveData: { pp?: number, maxpp?: number, disabled?: boolean },
		guide?: GalarMoveGuide,
		category?: Dex.CategoryName,
	} | null) {
		if (!props) {
			return <button class="movebutton" disabled>&nbsp;</button>;
		}
		const pp = props.moveData.maxpp ? `${props.moveData.pp!}/${props.moveData.maxpp}` : '\u2014';
		// Kanto's classic move menu: a plain row with the name, the type as a
		// word, and PP; the cursor appears on the one you point at.
		if (consoleModeOn() && !props.moveData.disabled) {
			const eff = props.guide?.eff;
			return <button
				data-cmd={props.cmd} data-tooltip={props.tooltip}
				class={`movebutton has-tooltip kt-move type-${props.type}`}
			>
				<span class="kt-move-name">{props.name}</span>
				<span class="kt-type">{props.type}</span>
				<span class="kt-pp">PP {pp}</span>
				{eff && <span class={`kt-eff kt-eff-${eff.key}`}>{eff.label}</span>}
				<small class="type kt-sr"><span class="effectiveness-icon">{props.tags}</span></small>
			</button>;
		}
		// Paldea's move bars: a slanted type-colored bar, the type icon on a
		// dark tab, PP, and an effectiveness arrow (its words for screen
		// readers and the tooltip).
		if (interfaceTheme() === 'paldea' && !props.moveData.disabled) {
			const eff = props.guide?.eff;
			return <button
				data-cmd={props.cmd} data-tooltip={props.tooltip}
				class={`movebutton has-tooltip pd-move type-${props.type}`}
			>
				<span class="pd-move-bg" aria-hidden></span>
				<span class="pd-move-tab">{PSIcon({ type: props.type })}</span>
				<span class="pd-move-name">{props.name}</span>
				<small class="pp">PP {pp}</small>
				{eff && <span class={`pd-eff pd-eff-${eff.key}`} title={eff.label}>
					<span aria-hidden>{eff.icon}</span><span class="pd-sr">{eff.label}</span>
				</span>}
				<small class="type pd-sr">{props.type} <span class="effectiveness-icon">{props.tags}</span></small>
			</button>;
		}
		// Alola's move pills: the type icon, the name over the effectiveness
		// label Sun and Moon introduced, and PP.
		if (interfaceTheme() === 'alola' && !props.moveData.disabled) {
			const eff = props.guide?.eff;
			return <button
				data-cmd={props.cmd} data-tooltip={props.tooltip}
				class={`movebutton has-tooltip al-move type-${props.type}`}
			>
				<span class="al-move-name">{props.name}</span>
				{eff && <span class={`al-eff al-eff-${eff.key}`}>{eff.label}</span>}
				<span class="al-move-type">{PSIcon({ type: props.type })}</span>
				<small class="pp">PP {pp}</small>
				<small class="type al-sr">{props.type} <span class="effectiveness-icon">{props.tags}</span></small>
			</button>;
		}
		// Galar's move cards: what the move is, in words, and how well it
		// works against the foe, as the badge Sword and Shield show.
		if (props.guide && interfaceTheme() === 'galar') {
			const eff = props.guide.eff;
			return <button
				data-cmd={props.cmd} data-tooltip={props.tooltip}
				class={`movebutton has-tooltip gx-move ${props.moveData.disabled ? 'disabled' : `type-${props.type}`}`}
				aria-disabled={props.moveData.disabled}
			>
				<span class="gx-move-name">{props.name}</span>
				{eff && <span class={`gx-eff gx-eff-${eff.key}`}>
					<span aria-hidden>{eff.icon}</span> {eff.label}
				</span>}
				<small class="type">{props.type} <span class="effectiveness-icon">{props.tags}</span></small>
				<small class="gx-move-meta">{props.guide.meta}</small>
				<small class="pp">PP {pp}</small>
			</button>;
		}
		// Unova's move capsules: the Black and White era's own type badge and
		// category icon, as the games' move list drew them.
		if (interfaceTheme() === 'unova' && !props.moveData.disabled) {
			return <button
				data-cmd={props.cmd} data-tooltip={props.tooltip}
				class={`movebutton has-tooltip ux-move type-${props.type}`}
			>
				<span class="ux-move-name">{props.name}</span>
				<span class="ux-move-foot">
					{PSIcon({ type: props.type })}
					{props.category && PSIcon({ category: props.category })}
					<small class="pp">{pp}</small>
				</span>
				<small class="type ux-sr">{props.type} <span class="effectiveness-icon">{props.tags}</span></small>
			</button>;
		}
		return <button
			data-cmd={props.cmd} data-tooltip={props.tooltip}
			class={`movebutton has-tooltip ${props.moveData.disabled ? 'disabled' : `type-${props.type}`}`}
			aria-disabled={props.moveData.disabled}
		>
			{props.name}<br />
			<small class="type">{props.type} <span class="effectiveness-icon">{props.tags}</span></small> {}
			<small class="pp">{pp}</small>&nbsp;
		</button>;
	}
	renderPokemonButton(props: {
		pokemon: Pokemon | ServerPokemon | null, cmd: string, noHPBar?: boolean, disabled?: boolean | 'fade', tooltip: string,
	}) {
		const pokemon = props.pokemon;
		if (!pokemon) {
			return <button
				data-cmd={props.cmd} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
				aria-disabled={props.disabled}
				style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
			>
				(empty slot)
			</button>;
		}

		let hpColorClass;
		switch (BattleScene.getHPColor(pokemon)) {
		case 'y': hpColorClass = 'hpbar hpbar-yellow'; break;
		case 'r': hpColorClass = 'hpbar hpbar-red'; break;
		default: hpColorClass = 'hpbar'; break;
		}

		return <button
			data-cmd={props.cmd} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
			aria-disabled={props.disabled}
			style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
		>
			{PSIcon({ pokemon })}
			{pokemon.name}
			{
				!props.noHPBar && !pokemon.fainted &&
				<span class={hpColorClass}>
					<span style={{ width: Math.round(pokemon.hp * 92 / pokemon.maxhp) || 1 }}></span>
				</span>
			}
			{!props.noHPBar && pokemon.status && <span class={`status ${pokemon.status}`}></span>}
		</button>;
	}
	renderMoveMenu(choices: BattleChoiceBuilder, overlayVersion?: boolean) {
		const moveRequest = choices.currentMoveRequest()!;

		const canDynamax = moveRequest.canDynamax && !choices.alreadyMax;
		const canMegaEvo = moveRequest.canMegaEvo && !choices.alreadyMega;
		const canMegaEvoX = moveRequest.canMegaEvoX && !choices.alreadyMega;
		const canMegaEvoY = moveRequest.canMegaEvoY && !choices.alreadyMega;
		const canZMove = moveRequest.zMoves && !choices.alreadyZ;
		const canUltraBurst = moveRequest.canUltraBurst;
		const canTerastallize = moveRequest.canTerastallize;

		const maybeDisabled = moveRequest.maybeDisabled;
		const maybeLocked = moveRequest.maybeLocked;

		// The move menu says which gimmick is switched on, so the moves can show it.
		const gimmickOn = choices.current.max ? (moveRequest.gigantamax ? ' gimmick-on-gmax' : ' gimmick-on-max') :
			choices.current.z ? ' gimmick-on-z' :
			choices.current.tera ? ' gimmick-on-tera' :
			(choices.current.mega || choices.current.megax || choices.current.megay || choices.current.ultra) ? ' gimmick-on-mega' : '';
		return <div class={`movemenu${gimmickOn}`} data-tera-type={choices.current.tera ? canTerastallize || undefined : undefined}>
			{maybeDisabled && <p><em class="movewarning">
				You <strong>might</strong> have some moves disabled, so you won't be able to cancel an attack!
			</em></p>}
			{maybeLocked && <p><em class="movewarning">
				You <strong>might</strong> be locked into a move. {}
				<button class="button" data-cmd="/choose testfight">Try Fight button</button> {}
				(prevents switching if you're locked)
			</em></p>}
			{!overlayVersion && this.renderMoveControls(moveRequest, choices)}
			<div class="megaevo-box">
				{canDynamax && <label class={`megaevo gimmick-${moveRequest.gigantamax ? 'gmax' : 'max'}${choices.current.max ? ' cur' : ''}`}>
					<input type="checkbox" name="max" checked={choices.current.max} onChange={this.toggleBoostedMove} /> {}
					{moveRequest.gigantamax ? 'Gigantamax' : 'Dynamax'}
				</label>}
				{canMegaEvo && <label class={`megaevo gimmick-mega${choices.current.mega ? ' cur' : ''}`}>
					<input type="checkbox" name="mega" checked={choices.current.mega} onChange={this.toggleBoostedMove} /> {}
					Mega Evolution
				</label>}
				{canMegaEvoX && <label class={`megaevo gimmick-mega${choices.current.megax ? ' cur' : ''}`}>
					<input type="checkbox" name="megax" checked={choices.current.megax} onChange={this.toggleBoostedMove} /> {}
					Mega Evolution X
				</label>}
				{canMegaEvoY && <label class={`megaevo gimmick-mega${choices.current.megay ? ' cur' : ''}`}>
					<input type="checkbox" name="megay" checked={choices.current.megay} onChange={this.toggleBoostedMove} /> {}
					Mega Evolution Y
				</label>}
				{canUltraBurst && <label class={`megaevo gimmick-ultra${choices.current.ultra ? ' cur' : ''}`}>
					<input type="checkbox" name="ultra" checked={choices.current.ultra} onChange={this.toggleBoostedMove} /> {}
					Ultra Burst
				</label>}
				{canZMove && <label class={`megaevo gimmick-z${choices.current.z ? ' cur' : ''}`}>
					<input type="checkbox" name="z" checked={choices.current.z} onChange={this.toggleBoostedMove} /> {}
					Z-Power
				</label>}
				{canTerastallize && <label
					class={`megaevo gimmick-tera${choices.current.tera ? ' cur' : ''}`} data-tera-type={canTerastallize}
				>
					<input type="checkbox" name="tera" checked={choices.current.tera} onChange={this.toggleBoostedMove} /> {}
					Tera {PSIcon({ type: canTerastallize, new: true, tera: true })}
				</label>}
			</div>
			{overlayVersion && this.renderMoveControls(moveRequest, choices)}
		</div>;
	}
	renderMoveControls(active: BattleRequestActivePokemon, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		const dex = battle.dex;
		const pokemonIndex = choices.index();
		const activeIndex = battle.mySide.n > 1 ? pokemonIndex + battle.pokemonControlled : pokemonIndex;
		const serverPokemon = choices.request.side!.pokemon[pokemonIndex];
		const valueTracker = new ModifiableValue(battle, battle.nearSide.active[activeIndex]!, serverPokemon);
		const tooltips = (battle.scene as BattleScene).tooltips;

		if (choices.current.max || (active.maxMoves && !active.canDynamax)) {
			if (!active.maxMoves) {
				return <div class="message-error">Maxed with no max moves</div>;
			}
			const gmax = active.gigantamax && dex.moves.get(active.gigantamax);
			return active.moves.map((moveData, i) => {
				const move = dex.moves.get(moveData.name);
				const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker, gmax || true);
				let maxMoveData: { name: string, id: ID } = active.maxMoves![i];
				if (maxMoveData.name !== 'Max Guard') {
					maxMoveData = tooltips.getMaxMoveFromType(moveType, gmax);
				}
				const gmaxTooltip = maxMoveData.id.startsWith('gmax') ? `|${maxMoveData.id}` : ``;
				const tooltip = `maxmove|${moveData.name}|${pokemonIndex}${gmaxTooltip}`;
				return this.renderMoveButton({
					name: maxMoveData.name,
					cmd: `/move ${i + 1} max`,
					type: moveType,
					tags,
					tooltip,
					moveData,
				});
			});
		}

		if (choices.current.z) {
			if (!active.zMoves) {
				return <div class="message-error">No Z moves</div>;
			}
			return active.moves.map((moveData, i) => {
				const zMoveData = active.zMoves![i];
				if (!zMoveData) {
					return this.renderMoveButton(null);
				}
				const specialMove = dex.moves.get(zMoveData.name);
				const move = specialMove.exists ? specialMove : dex.moves.get(moveData.name);
				const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker);
				const tooltip = `zmove|${moveData.name}|${pokemonIndex}`;
				return this.renderMoveButton({
					name: zMoveData.name,
					cmd: `/move ${i + 1} zmove`,
					type: moveType,
					tags,
					tooltip,
					moveData: { pp: 1, maxpp: 1 },
				});
			});
		}

		const special = choices.moveSpecial(choices.current);
		const source = battle.nearSide.active[activeIndex];
		return active.moves.map((moveData, i) => {
			const move = dex.moves.get(moveData.name);
			const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker);
			const tooltip = `move|${moveData.name}|${pokemonIndex}`;
			return this.renderMoveButton({
				name: move.name,
				cmd: `/move ${i + 1}${special}`,
				type: moveType,
				tags,
				tooltip,
				moveData,
				guide: this.galarMoveGuide(move, moveType, source),
				category: move.category,
			});
		});
	}
	/** Galar's move guide: category, power and accuracy in words, and the effectiveness badge. */
	galarMoveGuide(move: Dex.Move, moveType: Dex.TypeName, source: Pokemon | null): GalarMoveGuide | undefined {
		const battle = this.props.room.battle;
		if (!(['galar', 'alola', 'paldea'].includes(interfaceTheme()) || consoleModeOn()) || battle.hardcoreMode) return undefined;
		const parts: string[] = [move.category];
		if (move.category !== 'Status') parts.push(move.basePower ? `Power ${move.basePower}` : 'Power varies');
		parts.push(move.accuracy === true ? 'Never misses' : `${move.accuracy}% accurate`);
		let eff: GalarMoveGuide['eff'];
		const foes = battle.farSide.active.filter(p => p && !p.fainted) as Pokemon[];
		if (source && foes.length === 1) {
			const tooltips = (battle.scene as BattleScene).tooltips;
			const value = tooltips.getMoveEffectiveness(source, move, moveType, move.category, foes[0]);
			if (value === 0) {
				eff = { key: 'none', icon: '\u00d7', label: 'No effect' };
			} else if (value !== null && move.category !== 'Status') {
				eff = value > 1 ? { key: 'high', icon: '\u25b2', label: 'Super effective' } :
					value < 1 ? { key: 'low', icon: '\u25bc', label: 'Not very effective' } :
					{ key: 'ok', icon: '\u25cf', label: 'Effective' };
			}
		}
		return { meta: parts.join(' \u00b7 '), eff };
	}

	renderMoveTargetControls(request: BattleMoveRequest, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		let moveTarget = choices.currentMove()?.target;
		if ((moveTarget === 'adjacentAlly' || moveTarget === 'adjacentFoe') && battle.gameType === 'freeforall') {
			moveTarget = 'normal';
		}
		const moveChoice = choices.stringChoice(choices.current);

		const userSlot = choices.index() + Math.floor(battle.mySide.n / 2) * battle.pokemonControlled;
		const userSlotCross = battle.farSide.active.length - 1 - userSlot;

		return <>
			{battle.farSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentFoe') {
					if (Math.abs(userSlotCross - i) > 1) disabled = true;
				}

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} +${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|1|${i}`,
				});
			}).reverse()}
			<div style={{ clear: 'left' }}></div>
			{battle.nearSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentFoe') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					if (Math.abs(userSlot - i) > 1) disabled = true;
				}
				if (moveTarget !== 'adjacentAllyOrSelf' && userSlot === i) disabled = true;

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} -${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|0|${i}`,
				});
			})}
		</>;
	}
	renderSwitchMenu(
		request: BattleMoveRequest | BattleSwitchRequest, choices: BattleChoiceBuilder, ignoreTrapping?: boolean
	) {
		const numActive = choices.requestLength();
		const maybeTrapped = !ignoreTrapping && choices.currentMoveRequest()?.maybeTrapped;
		const trapped = !ignoreTrapping && !maybeTrapped && choices.currentMoveRequest()?.trapped;
		const isReviving = choices.isReviving();

		return <div class="switchmenu">
			{maybeTrapped && <em class="movewarning">
				You <strong>might</strong> be trapped, so you won't be able to cancel a switch!<br />
			</em>}
			{trapped && <em class="movewarning">
				You're <strong>trapped</strong> and cannot switch!<br />
			</em>}
			{isReviving && <em class="movewarning">
				Choose a Pokémon to revive!<br />
			</em>}
			{request.side.pokemon.map((serverPokemon, i) => {
				let cantSwitch = trapped || i < numActive || choices.alreadySwitchingIn.includes(i + 1) || serverPokemon.fainted;
				if (isReviving) cantSwitch = !serverPokemon.fainted || choices.alreadySwitchingIn.includes(i + 1);
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch ${i + 1}`,
					disabled: cantSwitch,
					tooltip: `switchpokemon|${i}`,
				});
			})}
			{request.ally?.pokemon?.map((serverPokemon, i) => {
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch notMine`,
					disabled: true,
					tooltip: `allypokemon|${i}`,
				});
			})}
		</div>;
	}
	renderTeamPreviewChooser(request: | BattleTeamRequest, choices: BattleChoiceBuilder) {
		return request.side.pokemon.map((serverPokemon, i) => {
			const cantSwitch = choices.alreadySwitchingIn.includes(i + 1);
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${i + 1}`,
				disabled: cantSwitch && 'fade',
				tooltip: `switchpokemon|${i}`,
			});
		});
	}
	renderTeamList(overlayVersion = false) {
		const team = this.team;
		if (!team) return;
		return <div class="switchcontrols">
			{!overlayVersion && <h3 class="switchselect">Team</h3>}
			<div class="switchmenu">
				{team.map((serverPokemon, i) => {
					return this.renderPokemonButton({
						pokemon: serverPokemon,
						cmd: "",
						disabled: true,
						tooltip: `switchpokemon|${i}`,
					});
				})}
			</div>
		</div>;
	}
	renderChosenTeam(request: BattleTeamRequest, choices: BattleChoiceBuilder) {
		return choices.alreadySwitchingIn.map(slot => {
			const serverPokemon = request.side.pokemon[slot - 1];
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${slot}`,
				tooltip: `switchpokemon|${slot - 1}`,
			});
		});
	}
	renderOldChoices(request: BattleRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		if (!choices) return null; // should not happen
		if (
			(request.requestType !== 'move' && request.requestType !== 'switch' && request.requestType !== 'team') ||
			choices.isEmpty()
		) {
			return null;
		}

		let buf: preact.ComponentChild[] = [
			<button data-cmd="/cancelone" class="button"><i class="fa fa-chevron-left" aria-hidden></i> Back</button>, ' ',
		];
		if (choices.isDone() && (
			choices.noCancel || this.props.room.battle.hardcoreMode ||
			(choices.choices.length <= 1 && !overlayVersion)
		)) {
			buf = [];
		}

		if (choices.serializedChoice) {
			if (choices.serializedChoice === 'default') {
				return [`Automatic choice`, <br />];
			}
			return [`Unrecognized choice from server: `, <code>{choices.serializedChoice}</code>, <br />];
		}

		const battle = this.props.room.battle;
		let teamList = false;
		for (let i = 0; i < choices.choices.length; i++) {
			const choiceString = choices.choices[i];
			if (choiceString === "testfight") {
				buf.push(`${request.side.pokemon[i].name} is locked into a move.`);
				return buf;
			}
			let choice;
			try {
				choice = choices.parseChoice(choiceString, i);
			} catch (err: any) {
				buf.push(<span class="message-error">{err.message}</span>);
			}
			if (!choice) continue;
			const pokemon = request.side.pokemon[i];
			const active = request.requestType === 'move' ? request.active[i] : null;
			if (choice.choiceType === 'move') {
				buf.push(`${pokemon.name} will `);
				if (choice.mega) buf.push(<strong>Mega</strong>, ` Evolve and `);
				if (choice.megax) buf.push(<strong>Mega</strong>, ` Evolve (X) and `);
				if (choice.megay) buf.push(<strong>Mega</strong>, ` Evolve (Y) and `);
				if (choice.ultra) buf.push(<strong>Ultra</strong>, ` Burst and `);
				if (choice.tera) buf.push(`Terastallize (`, <strong>{active?.canTerastallize || '???'}</strong>, `) and `);
				if (choice.max && active?.canDynamax) buf.push(active?.gigantamax ? `Gigantamax and ` : `Dynamax and `);
				buf.push(`use `, <strong>{choices.currentMove(choice, i)?.name}</strong>);
				if (choice.targetLoc > 0) {
					const target = battle.farSide.active[choice.targetLoc - 1];
					if (!target) {
						buf.push(` at slot ${choice.targetLoc}`);
					} else {
						buf.push(` at ${target.name}`);
					}
				} else if (choice.targetLoc < 0) {
					const target = battle.nearSide.active[-choice.targetLoc - 1];
					const ally = battle.gameType !== 'freeforall' ? 'ally' : '';
					if (!target) {
						buf.push(` at ${ally} slot ${choice.targetLoc}`);
					} else {
						buf.push(` at ${ally} ${target.name}`);
					}
				}
				buf.push(`.`);
			} else if (choice.choiceType === 'switch') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				if (choices.isReviving(i)) {
					buf.push(`${pokemon.name} will revive `, <strong>{target.name}</strong>, `.`);
				} else {
					buf.push(`${pokemon.name} will switch to `, <strong>{target.name}</strong>, `.`);
				}
			} else if (choice.choiceType === 'shift') {
				buf.push(`${pokemon.name} will `, <strong>shift</strong>, ` to the center.`);
			} else if (choice.choiceType === 'team') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				buf.push(teamList ? `, ` : `You picked `, <strong>{target.name}</strong>);
				teamList = true;
			}
			if (!teamList) buf.push(<br />);
		}
		if (teamList) {
			buf.push('.', <br />);
		}
		return buf;
	}
	overlayControlClass(overlay: 'move' | 'switch') {
		return `button ${overlay}-button${this.props.room.overlayActive === overlay ? ' cur' : ''}`;
	}
	renderPlayerAnimationControls(overlayVersion = false) {
		const room = this.props.room;
		if (overlayVersion) {
			const canSkip = !room.battle.hardcoreMode;
			return <>
				{canSkip && <div class="overlay-controls-skip">
					<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />Skip</button>
				</div>}
			</>;
		}
		return <div class="inline-controls">
			{!room.battle.hardcoreMode && <div class="whatdo" style="padding-bottom:0">
				<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />Skip animation</button>
			</div>}
			{this.renderTeamList()}
		</div>;
	}
	renderPlayerMoveControls(request: BattleMoveRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const room = this.props.room;
		const index = choices.index();
		const pokemon = request.side.pokemon[index];

		if (choices.current.move) {
			const moveName = choices.currentMove()?.name;
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class="button move-button cur"><strong>Battle</strong></button> {}
						<button class="button switch-button disabled"><strong>Switch</strong></button>
					</div>
					<div class="targetcontrols">
						<p class="overlay-message">
							{this.renderOldChoices(request, choices, true)}
							{pokemon.name} should use <strong>{moveName}</strong> at where?
						</p>
						<div class="switchmenu">
							{this.renderMoveTargetControls(request, choices)}
						</div>
					</div>
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					{pokemon.name} should use <strong>{moveName}</strong> at where? {}
				</div>
				<div class="switchcontrols">
					<div class="switchmenu">
						{this.renderMoveTargetControls(request, choices)}
					</div>
				</div>
			</div>;
		}

		const canShift = room.battle.gameType === 'triples' && index !== 1;

		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class={this.overlayControlClass('move')} data-cmd="/movemenu"><strong>Battle</strong></button> {}
					<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>Switch</strong></button>
				</div>
				{!room.overlayActive && <div class="whatdo">
					{this.renderOldChoices(request, choices, true)}
					What will <strong>{pokemon.name}</strong> do?
				</div>}
				{room.overlayActive === 'move' && <div class="movecontrols">
					{this.renderMoveMenu(choices, true)}
				</div>}
				{room.overlayActive === 'switch' && <div class="switchcontrols">
					{canShift && (
						<button data-cmd="/shift">Move to center</button>
					)}
					{this.renderSwitchMenu(request, choices)}
				</div>}
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				What will <strong>{pokemon.name}</strong> do?
			</div>
			<div class="movecontrols">
				<h3 class="moveselect">Battle</h3>
				{this.renderMoveMenu(choices)}
			</div>
			<div class="switchcontrols">
				{canShift && [
					<h3 class="shiftselect">Shift</h3>,
					<button data-cmd="/shift">Move to center</button>,
				]}
				<h3 class="switchselect">Switch</h3>
				{this.renderSwitchMenu(request, choices)}
			</div>
		</div>;
	}
	renderPlayerSwitchControls(request: BattleSwitchRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const pokemon = request.side.pokemon[choices.index()];
		const prompt = choices.isReviving() ?
			<>Who will <strong>{pokemon.name}</strong> revive?</> :
			<>Who will replace <strong>{pokemon.name}</strong>?</>;
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>Switch</strong></button>
				</div>
				<div class="switchcontrols">
					<p class="overlay-message">
						{this.renderOldChoices(request, choices, true)}
						{prompt}
					</p>
					{this.renderSwitchMenu(request, choices, true)}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">Switch</h3>
				{this.renderSwitchMenu(request, choices, true)}
			</div>
		</div>;
	}
	renderPlayerTeamPreviewControls(request: BattleTeamRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const prompt = choices.alreadySwitchingIn.length > 0 ? (
			[<button data-cmd="/cancelone" class="button"><i class="fa fa-chevron-left" aria-hidden></i> Back</button>,
				" What about the rest of your team? "]
		) : (
			"How will you start the battle? "
		);
		const chosenTeamSizeLabel = (request.chosenTeamSize || 0) > 1 ? ` / ${request.chosenTeamSize!}` : '';
		const chooseLabel = choices.alreadySwitchingIn.length <= 0 ?
			`lead${chosenTeamSizeLabel}` : `slot ${choices.alreadySwitchingIn.length + 1}${chosenTeamSizeLabel}`;
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>Team</strong></button>
				</div>
				<div class="teamcontrols">
					<p class="overlay-message">{prompt}</p>
					<h3 class="switchselect">Choose {chooseLabel}</h3>
					<div class="switchmenu">
						{this.renderTeamPreviewChooser(request, choices)}
						<div style="clear:left"></div>
					</div>
					{choices.alreadySwitchingIn.length > 0 && <>
						<h3 class="switchselect">Team so far</h3>
						<div class="switchmenu">
							{this.renderChosenTeam(request, choices)}
						</div>
					</>}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">
					Choose {chooseLabel}
				</h3>
				<div class="switchmenu">
					{this.renderTeamPreviewChooser(request, choices)}
					<div style="clear:left"></div>
				</div>
			</div>
			<div class="switchcontrols">
				{choices.alreadySwitchingIn.length > 0 && <h3 class="switchselect">Team so far</h3>}
				<div class="switchmenu">
					{this.renderChosenTeam(request, choices)}
				</div>
			</div>
		</div>;
	}
	renderPlayerControls(request: BattleRequest, overlayVersion = false) {
		const room = this.props.room;
		const atEnd = room.battle.atQueueEnd;
		if (!atEnd) return this.renderPlayerAnimationControls(overlayVersion);

		let choices = room.choices;
		if (!choices) return 'Error: Missing BattleChoiceBuilder';
		if (choices.request !== request) {
			choices = new BattleChoiceBuilder(request);
			room.choices = choices;
			room.overlayActive = null;
		}

		if (choices.isDone()) {
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>Team</strong></button>
					</div>
					{!room.overlayActive && <div class="whatdo">
						{this.renderOldChoices(request, choices, true)}
					</div>}
					{room.overlayActive === 'switch' && this.renderTeamList(true)}
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					<em>Waiting for opponent...</em> {choices.noCancel || room.battle.hardcoreMode ?
						null : <button data-cmd="/cancel" class="button">Cancel</button>}
				</div>
				{this.renderTeamList()}
			</div>;
		}
		if (request.side) {
			room.battle.myPokemon = request.side.pokemon;
			this.team = request.side.pokemon;
		}
		switch (request.requestType) {
		case 'move':
			return this.renderPlayerMoveControls(request, choices, overlayVersion);
		case 'switch':
			return this.renderPlayerSwitchControls(request, choices, overlayVersion);
		case 'team':
			return this.renderPlayerTeamPreviewControls(request, choices, overlayVersion);
		}
		return null;
	}

	/**
	 * Save to record (GMs of a Tabletop campaign only): which character fought
	 * on each side, guessed from the players' names, then onto their records
	 * (MyBattles! on the Rotom Phone).
	 */
	recordOpen = false;
	recordSaving = false;
	recordMessage = '';
	recordChoice = { template_id: 0, p1: 0, p2: 0, title: '' };
	openRecord = () => {
		const room = this.props.room;
		const campaigns = PSTabletop.gmCampaigns || [];
		const guess = (campaignId: number, name: string) => {
			const campaign = campaigns.find(c => c.id === campaignId);
			const matches = (campaign?.characters || []).filter(c => !c.npc && c.showdown === toID(name));
			return matches.length === 1 ? matches[0].id : 0;
		};
		// The campaign one of the players is in, else the first one.
		const campaign = campaigns.find(c => c.characters.some(ch => !ch.npc &&
			(ch.showdown === toID(room.battle.p1.name) || ch.showdown === toID(room.battle.p2.name)))) || campaigns[0];
		const id = campaign?.id || 0;
		this.recordChoice = {
			template_id: id, p1: guess(id, room.battle.p1.name), p2: guess(id, room.battle.p2.name), title: '',
		};
		this.recordOpen = true;
		this.recordMessage = '';
		this.forceUpdate();
	};
	changeRecord = (ev: Event) => {
		const target = ev.currentTarget as HTMLInputElement | HTMLSelectElement;
		const room = this.props.room;
		if (target.name === 'template_id') {
			this.recordChoice.template_id = Number(target.value);
			this.recordChoice.p1 = 0;
			this.recordChoice.p2 = 0;
			const campaign = (PSTabletop.gmCampaigns || []).find(c => c.id === this.recordChoice.template_id);
			const guess = (name: string) => {
				const found = (campaign?.characters || []).filter(c => !c.npc && c.showdown === toID(name));
				return found.length === 1 ? found[0].id : 0;
			};
			this.recordChoice.p1 = guess(room.battle.p1.name);
			this.recordChoice.p2 = guess(room.battle.p2.name);
		} else if (target.name === 'p1' || target.name === 'p2') {
			this.recordChoice[target.name] = Number(target.value);
		} else if (target.name === 'title') {
			this.recordChoice.title = target.value;
		}
		this.forceUpdate();
	};
	saveRecord = (ev: Event) => {
		ev.preventDefault();
		const room = this.props.room;
		if (this.recordSaving) return;
		this.recordSaving = true;
		this.forceUpdate();
		PSTabletop.saveRecord(room.id, room.battle, this.recordChoice).then(data => {
			this.recordMessage = data.message || 'Saved.';
			this.recordOpen = false;
		}, err => {
			this.recordMessage = err?.message || 'Could not reach Tabletop.';
		}).then(() => {
			this.recordSaving = false;
			this.forceUpdate();
		});
	};
	renderRecordForm() {
		const room = this.props.room;
		const campaigns = PSTabletop.gmCampaigns || [];
		const campaign = campaigns.find(c => c.id === this.recordChoice.template_id);
		const characters = campaign?.characters || [];
		const side = (key: 'p1' | 'p2') => <label class="record-side">
			<span>{room.battle[key].name}</span>
			<select name={key} class="button" value={this.recordChoice[key]} onChange={this.changeRecord}>
				<option value="0">Nobody (not on a record)</option>
				{characters.filter(c => !c.npc).length > 0 && <optgroup label="Trainers">
					{characters.filter(c => !c.npc).map(c => <option value={c.id}>{c.name}</option>)}
				</optgroup>}
				{characters.filter(c => c.npc).length > 0 && <optgroup label="NPC trainers">
					{characters.filter(c => c.npc).map(c => <option value={c.id}>{c.name}</option>)}
				</optgroup>}
			</select>
		</label>;
		return <form class="record-form" onSubmit={this.saveRecord}>
			<p class="record-form-title"><strong>Save to record</strong> <small>Which trainer fought on each side?</small></p>
			{campaigns.length > 1 && <label class="record-side">
				<span>Campaign</span>
				<select name="template_id" class="button" value={this.recordChoice.template_id} onChange={this.changeRecord}>
					{campaigns.map(c => <option value={c.id}>{c.name}</option>)}
				</select>
			</label>}
			{side('p1')}
			{side('p2')}
			<label class="record-side">
				<span>Title</span>
				<input
					name="title" class="textbox" maxLength={120} placeholder={`${room.battle.p1.name} vs. ${room.battle.p2.name}`}
					value={this.recordChoice.title} onInput={this.changeRecord}
				/>
			</label>
			<p>
				<button type="submit" class="button" disabled={this.recordSaving || (!this.recordChoice.p1 && !this.recordChoice.p2)}>
					<i class="fa fa-floppy-o" aria-hidden></i> {this.recordSaving ? 'Saving...' : 'Save to record'}
				</button> {}
				<button type="button" class="button" onClick={() => { this.recordOpen = false; this.forceUpdate(); }}>Cancel</button>
			</p>
		</form>;
	}
	renderAfterBattleControls() {
		const room = this.props.room;
		const isNotTiny = room.width > 700;
		const canRecord = !!PSTabletop.gmCampaigns?.length && room.battle.p1.name && room.battle.p2.name &&
			!room.id.startsWith('battle-tabletop-') && !room.id.startsWith('battle-uploaded-');
		return <div class="inline-controls">
			<p>
				<span style="float: right">
					<a
						onClick={this.handleDownloadReplay}
						href={`//${Config.routes.replays}/download`}
						class="button replayDownloadButton"
					>
						<i class="fa fa-download" aria-hidden></i> Download replay</a>
					<br />
					<br />
					<button class="button" data-cmd="/savereplay">
						<i class="fa fa-upload" aria-hidden></i> Upload and share replay
					</button>
				</span>

				<button class="button" data-cmd="/play" style="min-width:4.5em">
					<i class="fa fa-undo" aria-hidden></i><br />Replay
				</button> {}
				{canRecord && !this.recordOpen && <><button class="button record-button" onClick={this.openRecord}>
					<i class="fa fa-trophy" aria-hidden></i><br />Save to record
				</button> {}</>}
				{isNotTiny && !room.battle.hardcoreMode && <>
					<button class="button button-first" data-cmd="/ffto 0" style="margin-right:2px">
						<i class="fa fa-undo" aria-hidden></i><br />First turn
					</button>
					<button class="button button-first" data-cmd="/ffto -1">
						<i class="fa fa-step-backward" aria-hidden></i><br />Prev turn
					</button>
				</>}
			</p>
			{canRecord && this.recordOpen && this.renderRecordForm()}
			{this.recordMessage && <p class="record-message">{this.recordMessage}</p>}
			{room.side ? (
				<p>
					<button class="button" data-cmd="/close">
						<strong>Main menu</strong><br /><small>(closes this battle)</small>
					</button> {}
					<button class="button" data-cmd={`/closeand /challenge ${room.battle.farSide.id},${room.battle.tier}`}>
						<strong>Rematch</strong><br /><small>(closes this battle)</small>
					</button>
				</p>
			) : (
				<p>
					<button class="button" data-cmd="/switchsides"><i class="fa fa-random" aria-hidden></i> Switch viewpoint</button> {}
					{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">
						<i class="fa fa-random" aria-hidden></i> Go to turn
					</button>}
				</p>
			)}
		</div>;
	}

	handleDownloadReplay = (e: MouseEvent) => {
		let room = this.props.room;
		const target = e.currentTarget as HTMLAnchorElement;
		// download replay
		let filename = (room.battle.tier || 'Battle').replace(/[^A-Za-z0-9]/g, '');
		let date = new Date();
		filename += `-${date.getFullYear()}`;
		filename += `-${date.getMonth() >= 9 ? '' : '0'}${date.getMonth() + 1}`;
		filename += `-${date.getDate() >= 10 ? '' : '0'}${date.getDate()}`;
		filename += '-' + toID(room.battle.p1.name);
		filename += '-' + toID(room.battle.p2.name);
		target.href = window.BattleLog.createReplayFileHref(room);
		target.download = filename + '.html';
		e.stopPropagation();
	};

	/**
	 * Console mode's battle screen (the Kanto theme): the field as big as the
	 * screen allows, and beside it (below it on a narrow screen) a console's
	 * command menu. Fight, Pokemon and Forfeit first, then the moves or the
	 * party, each with a Back; the log and chat wait in a drawer.
	 */
	consoleLogOpen = false;
	toggleConsoleLog = () => {
		this.consoleLogOpen = !this.consoleLogOpen;
		this.forceUpdate();
	};
	consoleTargetBack = () => {
		const room = this.props.room;
		if (room.choices) room.choices.current.move = 0;
		room.update(null);
	};
	consoleForfeit = (ev: Event) => {
		const room = this.props.room;
		PS.join('forfeitbattle' as RoomID, { parentElem: ev.currentTarget as HTMLElement, parentRoomid: room.id });
	};
	/** The command menu shown, so a new one puts the highlight on its first choice. */
	consoleMenu = '';
	override componentDidUpdate() {
		super.componentDidUpdate();
		if (!consoleModeOn()) return;
		const menu = this.base?.querySelector<HTMLElement>('.kb-menu')?.dataset.kbMenu || '';
		if (menu === this.consoleMenu) return;
		this.consoleMenu = menu;
		setTimeout(() => PSConsole.focusIn(this.base?.querySelector<HTMLElement>('.kb-commands')), 30);
	}
	consoleHead(prompt: preact.ComponentChildren, sub?: preact.ComponentChildren) {
		return <header class="kb-head">
			<p class="kb-prompt">{prompt}</p>
			{sub && <p class="kb-sub">{sub}</p>}
		</header>;
	}
	renderConsoleCommands() {
		const room = this.props.room;
		if (!room.battle) return null;
		if (room.battle.ended) return this.renderConsoleAfter();
		if (room.side && room.request) {
			return <>{this.renderControllerTabs()}{this.renderConsolePlayer(room.request)}</>;
		}
		return this.renderConsolePlayback();
	}
	renderConsolePlayer(request: BattleRequest) {
		const room = this.props.room;
		if (!room.battle.atQueueEnd) {
			return <div class="kb-menu" key="playing" data-kb-menu="playing">
				{this.consoleHead('The turn is playing out')}
				{!room.battle.hardcoreMode && <div class="kb-list">
					<button class="button kb-cmd" data-cmd="/ffto end">
						<i class="fa fa-fast-forward" aria-hidden></i> Skip to the end
					</button>
				</div>}
			</div>;
		}
		let choices = room.choices;
		if (!choices) return null;
		if (choices.request !== request) {
			choices = new BattleChoiceBuilder(request);
			room.choices = choices;
			room.overlayActive = null;
		}
		if (request.side) {
			room.battle.myPokemon = request.side.pokemon;
			this.team = request.side.pokemon;
		}
		if (choices.isDone()) {
			return <div class="kb-menu" key="waiting" data-kb-menu="waiting">
				{this.consoleHead(<>Waiting for {room.battle.farSide.name || 'your opponent'}...</>, this.renderOldChoices(request, choices))}
				{!(choices.noCancel || room.battle.hardcoreMode) && <div class="kb-list">
					<button class="button kb-cmd" data-cmd="/cancel">
						<i class="fa fa-undo" aria-hidden></i> Change my choice
					</button>
				</div>}
			</div>;
		}
		switch (request.requestType) {
		case 'move':
			return this.renderConsoleMove(request, choices);
		case 'switch': {
			const pokemon = request.side.pokemon[choices.index()];
			return <div class="kb-menu is-party" key="switch" data-kb-menu="switch">
				{this.consoleHead(choices.isReviving() ?
					<>Who will <strong>{pokemon.name}</strong> revive?</> :
					<>Who will replace <strong>{pokemon.name}</strong>?</>, this.renderOldChoices(request, choices))}
				{this.renderSwitchMenu(request, choices, true)}
			</div>;
		}
		case 'team': {
			const picked = choices.alreadySwitchingIn.length;
			const size = request.chosenTeamSize || 0;
			return <div class="kb-menu is-party" key={`team${picked}`} data-kb-menu={`team${picked}`}>
				{this.consoleHead(picked ? 'Who comes next?' : 'Who will lead?',
					size > 1 ? `Pick ${size} Pokémon, in order.` : null)}
				<div class="switchmenu">{this.renderTeamPreviewChooser(request, choices)}</div>
				{picked > 0 && <button class="button kb-back" data-cmd="/cancelone" data-console-back>
					<i class="fa fa-chevron-left" aria-hidden></i> Back
				</button>}
			</div>;
		}
		}
		return null;
	}
	renderConsoleMove(request: BattleMoveRequest, choices: BattleChoiceBuilder) {
		const room = this.props.room;
		const index = choices.index();
		const pokemon = request.side.pokemon[index];
		const old = this.renderOldChoices(request, choices);
		if (choices.current.move) {
			return <div class="kb-menu is-targets" key={`target${index}`} data-kb-menu={`target${index}`}>
				{this.consoleHead(<>Use <strong>{choices.currentMove()?.name}</strong> on which Pokémon?</>, old)}
				<div class="switchmenu">{this.renderMoveTargetControls(request, choices)}</div>
				<button class="button kb-back" onClick={this.consoleTargetBack} data-console-back>
					<i class="fa fa-chevron-left" aria-hidden></i> Back
				</button>
			</div>;
		}
		if (room.overlayActive === 'move') {
			return <div class="kb-menu is-fight" key={`fight${index}`} data-kb-menu={`fight${index}`}>
				<div class="movecontrols">{this.renderMoveMenu(choices)}</div>
				<button class="button kb-back" data-cmd="/movemenu" data-console-back>
					<i class="fa fa-chevron-left" aria-hidden></i> Back
				</button>
			</div>;
		}
		if (room.overlayActive === 'switch') {
			const canShift = room.battle.gameType === 'triples' && index !== 1;
			return <div class="kb-menu is-party" key={`party${index}`} data-kb-menu={`party${index}`}>
				{this.consoleHead(<>Switch <strong>{pokemon.name}</strong> for...</>)}
				{canShift && <button class="button kb-cmd" data-cmd="/shift">Move to the center</button>}
				{this.renderSwitchMenu(request, choices)}
				<button class="button kb-back" data-cmd="/switchmenu" data-console-back>
					<i class="fa fa-chevron-left" aria-hidden></i> Back
				</button>
			</div>;
		}
		return <div class="kb-menu is-root" key={`root${index}`} data-kb-menu={`root${index}`}>
			{this.consoleHead(<>What will <strong>{pokemon.name}</strong> do?</>, old)}
			<div class="kb-root">
				<button class="button kb-big is-fight" data-cmd="/movemenu" data-console-default>
					<span class="kb-big-icon" aria-hidden></span> Fight
				</button>
				<button class="button kb-big is-party" data-cmd="/switchmenu">
					<span class="kb-big-icon" aria-hidden></span> Pokémon
				</button>
				<button class="button kb-big is-forfeit" onClick={this.consoleForfeit}>
					<span class="kb-big-icon" aria-hidden></span> Forfeit
				</button>
			</div>
		</div>;
	}
	renderConsolePlayback() {
		const room = this.props.room;
		const battle = room.battle;
		if (!battle.stepQueue.length) {
			return <div class="kb-menu" key="starting" data-kb-menu="starting">
				{this.consoleHead('The battle is about to begin')}
			</div>;
		}
		const atStart = !battle.started;
		const atEnd = battle.atQueueEnd;
		return <div class="kb-menu is-playback" key="playback" data-kb-menu="playback">
			{this.consoleHead(battle.turn ? `Turn ${battle.turn}` : 'Watching')}
			<div class="kb-player" data-console-region="row">
				{!battle.hardcoreMode && <>
					<button class="button kb-play-btn" data-cmd="/ffto 0" disabled={atStart} aria-label="First turn">
						<i class="fa fa-fast-backward" aria-hidden></i>
					</button>
					<button class="button kb-play-btn" data-cmd="/ffto -1" disabled={atStart} aria-label="Previous turn">
						<i class="fa fa-step-backward" aria-hidden></i>
					</button>
				</>}
				{atEnd || battle.paused ? (
					<button class="button kb-play-btn is-main" data-cmd="/play" disabled={atEnd} aria-label="Play" data-console-default>
						<i class="fa fa-play" aria-hidden></i>
					</button>
				) : (
					<button class="button kb-play-btn is-main" data-cmd="/pause" aria-label="Pause" data-console-default>
						<i class="fa fa-pause" aria-hidden></i>
					</button>
				)}
				{!battle.hardcoreMode && <>
					<button class="button kb-play-btn" data-cmd="/ffto +1" disabled={atEnd} aria-label="Next turn">
						<i class="fa fa-step-forward" aria-hidden></i>
					</button>
					<button class="button kb-play-btn" data-cmd="/ffto end" disabled={atEnd} aria-label="Last turn">
						<i class="fa fa-fast-forward" aria-hidden></i>
					</button>
				</>}
			</div>
			<div class="kb-list">
				<button class="button kb-cmd" data-cmd="/switchsides">
					<i class="fa fa-random" aria-hidden></i> Switch sides
				</button>
			</div>
		</div>;
	}
	renderConsoleAfter() {
		const room = this.props.room;
		const battle = room.battle;
		const canRecord = !!PSTabletop.gmCampaigns?.length && battle.p1.name && battle.p2.name &&
			!room.id.startsWith('battle-tabletop-') && !room.id.startsWith('battle-uploaded-');
		// The winner, from the battle's own |win| line.
		const winLine = battle.stepQueue.find(line => line.startsWith('|win|'));
		const winner = winLine ? winLine.slice(5) : '';
		const head = !winLine ? 'The battle is over' : !winner ? 'The battle ended in a tie' :
			room.side && toID(winner) === battle.mySide.id ? 'You won!' : `${winner} won`;
		return <div class="kb-menu is-after" key="after" data-kb-menu="after">
			{this.consoleHead(head)}
			<div class="kb-list">
				{room.side && <button
					class="button kb-cmd" data-cmd={`/closeand /challenge ${battle.farSide.id},${battle.tier}`} data-console-default
				>
					<i class="fa fa-repeat" aria-hidden></i> Rematch
				</button>}
				<button class="button kb-cmd" data-cmd="/play">
					<i class="fa fa-undo" aria-hidden></i> Watch it again
				</button>
				{canRecord && !this.recordOpen && <button class="button kb-cmd" onClick={this.openRecord}>
					<i class="fa fa-trophy" aria-hidden></i> Save to record
				</button>}
				<a class="button kb-cmd" onClick={this.handleDownloadReplay} href={`//${Config.routes.replays}/download`}>
					<i class="fa fa-download" aria-hidden></i> Download replay
				</a>
				<button class="button kb-cmd" data-cmd="/close">
					<i class="fa fa-home" aria-hidden></i> Main menu
				</button>
			</div>
			{canRecord && this.recordOpen && this.renderRecordForm()}
			{this.recordMessage && <p class="record-message">{this.recordMessage}</p>}
		</div>;
	}
	renderConsole(hardcoreStyle: preact.ComponentChild) {
		const room = this.props.room;
		const { battleWidth, battleHeight } = this.chooseLayout();
		const playing = room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid;
		return <PSPanelWrapper room={room} focusClick noScroll="hidden">
			{hardcoreStyle}
			<div class={`kb-screen${this.consoleLogOpen ? ' is-log-open' : ''}`}>
				<div class="kb-stage">
					<div class="kb-field" style={`width:${battleWidth}px;height:${battleHeight}px`}>
						<BattleDiv room={room} />
					</div>
				</div>
				<aside class="kb-panel" role="complementary" aria-label="Battle commands">
					<div class="kb-commands">
						{this.renderConsoleCommands()}
						{this.renderConnectError()}
					</div>
					<div class="kb-foot">
						{playing && <TimerButton room={room} top={0} />}
						<button class="button kb-log-toggle" onClick={this.toggleConsoleLog} aria-pressed={this.consoleLogOpen}>
							<i class="fa fa-comments" aria-hidden></i> {this.consoleLogOpen ? 'Hide log' : 'Log and chat'}
						</button>
					</div>
					<div class="kb-log" aria-hidden={!this.consoleLogOpen}>
						<ChatLog class="battle-log" room={room} noSubscription hasPreempt />
						<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} tinyLayout />
					</div>
				</aside>
			</div>
		</PSPanelWrapper>;
	}
	override render() {
		this.updateLayout();
		const room = this.props.room;
		const id = `room-${room.id}`;
		const hardcoreStyle = room.battle?.hardcoreMode ? <style
			dangerouslySetInnerHTML={{ __html: `#${id} .battle .turn, #${id} .battle-history { display: none !important; }` }}
		></style> : null;
		if (consoleModeOn()) return this.renderConsole(hardcoreStyle);
		const { layout, battleHeight, battleWidth, overlayControls } = this.chooseLayout();
		const overlayVersion = overlayControls && !!room.battle && !!room.side && !!room.request && !room.battle.ended;

		if (layout === 'scrolling') {
			// low-width-low-height layout
			// TODO: nicer phone horizontal layout
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<ChatLog
					class="battle-log hasuserlist" room={room} noSubscription hasPreempt bottom={0}
				>
					<div style="height:18px;position:relative">
						<ChatUserList room={room} top={0} minimized />
					</div>
					<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />
					<div style={`height:${battleHeight}px;width:${battleWidth}px;margin: 0 auto;position:relative`}>
						<BattleDiv room={room} />
					</div>
					{overlayVersion && <div class="overlay-controls" style="position:relative;height:0">
						{this.renderControls(true)}
					</div>}
					<div
						class={`battle-controls inline-battle${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label="Battle Controls"
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (layout === 'top-and-bottom') {
			// phone vertical layout
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div style={`position:relative;height:${battleHeight}px;width:${battleWidth}px;margin:0 auto`}>
					<BattleDiv room={room} />
				</div>
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:100%;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<ChatLog
					class="battle-log hasuserlist" room={room} top={battleHeight} noSubscription hasPreempt
				>
					<div
						class={`battle-controls${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label="Battle Controls"
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />
				<ChatUserList room={room} top={battleHeight} minimized />
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={battleHeight + 7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (room.width < 500) {
			// oldclient phone layout
			const showingChat = this.mobileChatShown;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden">
				{hardcoreStyle}
				<div class="scrollable-battle-container" style={`width:${battleWidth}px;${showingChat ? 'display:none;' : ''}`}>
					<BattleDiv room={room} />
					{overlayVersion && <div
						class="overlay-controls"
						style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
					>
						{this.renderControls(true)}
					</div>}
					<div class="battle-controls-container">
						<div
							class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
							role="complementary" aria-label="Battle Controls"
							style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
						>
							{(room.battle && !room.battle.ended && room.request &&
								room.battle.mySide.id === PS.user.userid) && <TimerButton room={room} top={0} />}
							{this.renderControls(false, overlayVersion)}
							{this.renderConnectError()}
						</div>
					</div>
				</div>
				<div style={!showingChat ? 'display:none;' : ''}>
					<ChatLog class="battle-log hasuserlist" room={room} noSubscription hasPreempt />
					<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} tinyLayout />
					<ChatUserList room={room} minimized />
				</div>
				{showingChat ? (
					<button class="battle-chat-toggle button" name="hideChat" onClick={this.showMobileBattle}>
						Battle <i class="fa fa-caret-right" aria-hidden></i>
					</button>
				) : (
					<button class="battle-chat-toggle button" name="showChat" onClick={this.showMobileChat}>
						<i class="fa fa-caret-left" aria-hidden></i> Chat
					</button>
				)}
			</PSPanelWrapper>;
		}

		// regular layout
		return <PSPanelWrapper room={room} focusClick noScroll="hidden">
			{hardcoreStyle}
			<div class="scrollable-battle-container" style={`width:${battleWidth}px`}>
				<BattleDiv room={room} />
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<div class="battle-controls-container">
					<div
						class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
						role="complementary" aria-label="Battle Controls"
						style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
					>
						{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
							<TimerButton room={room} top={0} />}
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</div>
			</div>
			<ChatLog
				class="battle-log hasuserlist" room={room} left={battleWidth} noSubscription hasPreempt
			>
				{}
			</ChatLog>
			<ChatTextEntry
				room={room} onMessage={this.send} onKey={this.onKey} left={battleWidth} tinyLayout={room.width < battleWidth + 340}
			/>
			<ChatUserList room={room} left={battleWidth} minimized />
		</PSPanelWrapper>;
	}
}

PS.addRoomType(BattlePanel, BattlesPanel);
